/**
 * Module for handling digital product variant display logic
 * Shows/hides the digital download form vs the "Add to Cart" form
 * based on whether the selected variant is a digital variant.
 * Works with the ODBM app's digital-content-ui extension.
 */

/**
 * Extracts variant ID from form input
 * @returns {string|null} The variant ID or null if not found
 */
const getVariantFromForm = () => {
  const input = document.querySelector(
    ".main-product-form input[name='id'], .product-form input[name='id']"
  );
  if (input?.value) {
    return input.value;
  }
  return null;
};

/**
 * Extracts variant ID from URL parameters
 * @param {string} url - The URL to parse
 * @returns {string|null} The variant ID or null if not found
 */
const getVariantFromUrl = (url) => {
  try {
    const urlParams = new URLSearchParams(new URL(url).search);
    return urlParams.get("variant");
  } catch (error) {
    console.error("Error parsing URL:", error);
    return null;
  }
};

/**
 * Gets the current variant from form or URL
 * @returns {string|null} The current variant ID
 */
const getCurrentVariant = () =>
  getVariantFromForm() || getVariantFromUrl(window.location.href);

/**
 * Sets the URL's variant param, keeping the rest of the query string (UTM tags).
 * @param {string} variantId - The variant ID to add to URL
 */
const updateUrlWithVariant = (variantId) => {
  if (!variantId) {
    return;
  }
  const url = new URL(window.location.href);
  url.searchParams.set("variant", variantId);
  window.history.replaceState(
    {},
    "",
    `${url.pathname}${url.search}${url.hash}`
  );
};

/**
 * The current path with any non-variant params from `search` that it lacks added back.
 * Skips `variant`: the vendor writes the bare path on purpose for an unavailable combo.
 * @param {string} search - an earlier location.search
 * @returns {string}
 */
const withParamsFrom = (search) => {
  const url = new URL(window.location.href);
  for (const [key, value] of new URLSearchParams(search)) {
    if (key !== "variant" && !url.searchParams.has(key)) {
      url.searchParams.set(key, value);
    }
  }
  return `${url.pathname}${url.search}${url.hash}`;
};

/**
 * Set an element's display style, no-op if the element is missing.
 * @param {Element|null} element
 * @param {string} value - any valid CSS display value, or "" to reset
 */
const setDisplay = (element, value) => {
  if (element) {
    element.style.display = value;
  }
};

/**
 * Toggle the sticky-atc-bar hidden-digital class based on variant kind.
 * @param {Element|null} element
 * @param {boolean} isDigital
 */
const STICKY_HIDDEN_DIGITAL = "sticky-atc-bar--hidden-digital";
const toggleHiddenDigitalClass = (element, isDigital) => {
  if (!element) {
    return;
  }
  element.classList.toggle(STICKY_HIDDEN_DIGITAL, isDigital);
};

/**
 * Main product variant handler class
 */
class ProductVariantHandler {
  constructor() {
    this.variantData = this.getVariantData();
    this.addToCartForm = document.querySelector(
      ".f-main-product-form, .product-form"
    );
    this.originalPushState = history.pushState;
    this.originalReplaceState = history.replaceState;
    this.initialVariant = getCurrentVariant();

    this.init();
  }

  /**
   * Get variant data from DOM
   * @returns {Object} Parsed variant data
   */
  getVariantData() {
    try {
      const variantElement = document.getElementById("variant-data");
      if (!variantElement) {
        throw new Error("Variant data element not found");
      }
      return JSON.parse(variantElement.textContent);
    } catch (error) {
      console.error("Error parsing variant data:", error);
      return { digital_variants: [] };
    }
  }

  /**
   * Whether the given variant ID is in the configured digital variants list.
   * @param {string|null} variantId
   * @returns {boolean}
   */
  isDigitalVariant(variantId) {
    return Boolean(this.variantData?.digital_variants?.includes(variantId));
  }

  /**
   * Correct a stale ?variant=<id>. Does NOT add one when absent: the selected
   * variant is read from the form, and the write triggers handleUrlChange's reload.
   * @param {string|null} currentVariant
   */
  syncVariantInUrl(currentVariant) {
    if (!currentVariant) {
      return;
    }
    const param = new URL(window.location.href).searchParams.get("variant");
    if (param !== null && param !== currentVariant) {
      updateUrlWithVariant(currentVariant);
    }
  }

  /**
   * Toggle the cart form, digital download form, sticky bar, and both quantity
   * controls based on whether the currently selected variant is digital.
   * Hide quantity for digital — meaningless for a single-copy download — and
   * restore it for physical variants. That covers the vendor Quantity selector
   * block and the ODBM Special Quantity block (bulk shortcuts read off the
   * odbm.special_edition_quantities metafield); the latter is hidden at its
   * BLOCK wrapper, not its inner picker, so the block's own vertical margin
   * goes with it. Restores to "" rather than a fixed display value, letting
   * each element fall back to its stylesheet default.
   */
  updateDigitalExtensionLogic() {
    const currentVariant = getCurrentVariant();
    const isDigital = this.isDigitalVariant(currentVariant);
    const digitalDownloadForm = document.querySelector(
      "#digital-download-form"
    );
    const stickyBar = document.querySelector(".sticky-atc-bar");
    const quantitySelector = document.querySelector(
      ".f-product-single__info-quantity"
    );
    const specialQuantity = document.querySelector(
      ".f-product-single__block--odbm_special_quantity"
    );

    setDisplay(this.addToCartForm, isDigital ? "none" : "block");
    setDisplay(digitalDownloadForm, isDigital ? "block" : "none");
    setDisplay(quantitySelector, isDigital ? "none" : "");
    setDisplay(specialQuantity, isDigital ? "none" : "");
    toggleHiddenDigitalClass(stickyBar, isDigital);

    this.syncVariantInUrl(currentVariant);
  }

  /**
   * Handle URL changes and update UI accordingly
   */
  handleUrlChange = () => {
    const currentUrl = window.location.href;
    const currentVariant = getVariantFromUrl(currentUrl);

    if (!currentVariant) {
      return;
    }

    this.updateDigitalExtensionLogic();

    if (currentVariant !== this.initialVariant) {
      this.restoreHistoryMethods();
      // replace(), not href: the vendor's replaceState already wrote this URL to history.
      window.location.replace(currentUrl);
    }
  };

  /**
   * Override history methods to detect URL changes
   */
  overrideHistoryMethods() {
    history.pushState = (...args) => {
      this.originalPushState.apply(history, args);
      this.handleUrlChange();
    };

    // The vendor's updateURL writes `?variant=<id>` alone; restore the UTM tags it drops.
    history.replaceState = (...args) => {
      const previousSearch = window.location.search;
      this.originalReplaceState.apply(history, args);
      this.originalReplaceState.call(
        history,
        history.state,
        "",
        withParamsFrom(previousSearch)
      );
      this.handleUrlChange();
    };
  }

  /**
   * Restore original history methods
   */
  restoreHistoryMethods() {
    history.pushState = this.originalPushState;
    history.replaceState = this.originalReplaceState;
  }

  /**
   * Set up MutationObserver for variant input changes
   */
  setupVariantObserver() {
    const input = document.querySelector(
      ".main-product-form input[name='id'], .product-form input[name='id']"
    );
    if (input) {
      let lastValue = input.value;
      const observer = new MutationObserver(() => {
        if (input.value !== lastValue) {
          lastValue = input.value;
          this.updateDigitalExtensionLogic();
        }
      });
      observer.observe(input, { attributes: true, attributeFilter: ["value"] });
      input.addEventListener("change", () => {
        if (input.value !== lastValue) {
          lastValue = input.value;
          this.updateDigitalExtensionLogic();
        }
      });
    }
  }

  /**
   * Set up observer for digital form — watches for the app extension to render
   */
  setupDigitalFormObserver() {
    // Look for the digital download form's parent container generically
    // The ODBM app extension renders #digital-download-form inside its block
    const productInfo = document.querySelector(".f-product-single, .product");
    if (productInfo) {
      const observer = new MutationObserver((mutationsList) => {
        for (const mutation of mutationsList) {
          if (mutation.type === "childList") {
            for (const node of mutation.addedNodes) {
              if (
                node.nodeType === 1 &&
                (node.id === "digital-download-form" ||
                  node.querySelector?.("#digital-download-form"))
              ) {
                this.updateDigitalExtensionLogic();
              }
            }
          }
        }
      });
      observer.observe(productInfo, { childList: true, subtree: true });

      // Check if form already exists
      const immediateForm = document.getElementById("digital-download-form");
      if (immediateForm) {
        this.updateDigitalExtensionLogic();
      } else {
        // Poll briefly for late-loading app extensions
        let pollCount = 0;
        const maxPolls = 20;
        const pollInterval = setInterval(() => {
          const polledForm = document.getElementById("digital-download-form");
          if (polledForm) {
            this.updateDigitalExtensionLogic();
            clearInterval(pollInterval);
          } else if (++pollCount >= maxPolls) {
            clearInterval(pollInterval);
          }
        }, 100);
      }
    }
  }

  /**
   * Set up pub/sub event listener for Zest theme variant changes
   */
  setupPubSubListener() {
    // Zest uses FoxThemeEvents for pub/sub
    if (window.FoxThemeEvents?.subscribe) {
      window.FoxThemeEvents.subscribe("variant:change", () => {
        this.updateDigitalExtensionLogic();
      });
    }
    // Also listen for standard Shopify pub/sub
    if (window.subscribe && window.PUB_SUB_EVENTS?.variantChange) {
      window.subscribe(window.PUB_SUB_EVENTS.variantChange, () => {
        this.updateDigitalExtensionLogic();
      });
    }
  }

  /**
   * Initialize event listeners and observers
   */
  init() {
    this.overrideHistoryMethods();
    this.setupVariantObserver();
    this.setupDigitalFormObserver();
    this.setupPubSubListener();

    // Run on page load
    this.updateDigitalExtensionLogic();

    // Clean up on page unload
    window.addEventListener("unload", () => {
      this.restoreHistoryMethods();
    });
  }
}

// Initialize the handler when DOM is ready
document.addEventListener("DOMContentLoaded", () => {
  new ProductVariantHandler();
});
